import { useEffect, useState } from "react";
import { alertsApi } from "@/lib/api-client";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Bell, Siren } from "lucide-react";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const PRIORITY = {
  critical: "#EF4444",
  high: "#F59E0B",
  medium: "#3B82F6",
  low: "#71717A",
};

export default function AlertsCenter({ refreshKey }) {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const items = await alertsApi.list();
      setAlerts(items);
    } catch {}
  };

  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = alerts.filter((a) => !a.read).length;

  const markRead = async (id) => {
    await alertsApi.markRead(id);
    load();
  };

  const testAlert = async () => {
    try {
      await alertsApi.create({
        title: "Test high-priority alert",
        body: "This simulates an Executor request over Auto-Pilot budget.",
        priority: "high",
        channel: "in_app",
      });
      toast.success("Alert dispatched");
      load();
    } catch { toast.error("Alert failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="alerts-trigger"
          className="relative w-9 h-9 flex items-center justify-center border border-white/10 hover:border-amber-500/50 transition-colors uc-focus"
          style={{ background: "var(--uc-surface-2)" }}
        >
          <Bell size={15} className="text-white/80"/>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center bg-amber-400 text-black font-mono-uc text-[9px] font-bold rounded-none" data-testid="alerts-badge">
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-6" data-testid="alerts-drawer">
        <SheetHeader>
          <div className="uc-chip mb-2 w-fit"><Siren size={11}/> DIRECT ALERTS</div>
          <SheetTitle className="font-serif-display text-2xl text-white">Command Alerts</SheetTitle>
          <p className="text-white/50 text-xs">High-priority events routed via in-app, SMS, or push.</p>
        </SheetHeader>
        <Button data-testid="alerts-test-btn" onClick={testAlert} className="uc-btn-amber rounded-none h-9 mt-4 w-full text-xs">
          Dispatch Test Alert
        </Button>
        <div className="mt-4 space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto uc-scroll -mr-2 pr-2">
          {alerts.map((a) => (
            <div
              key={a.id}
              onClick={() => !a.read && markRead(a.id)}
              className={`p-3 border cursor-pointer transition-colors ${a.read ? "border-white/5 bg-transparent" : "border-white/15 bg-white/[0.03]"}`}
              style={{ borderLeft: `2px solid ${PRIORITY[a.priority] || "#71717A"}` }}
              data-testid={`alert-${a.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm text-white font-medium">{a.title}</span>
                <span className="font-mono-uc text-[9px] uppercase tracking-wider" style={{ color: PRIORITY[a.priority] }}>{a.priority}</span>
              </div>
              <p className="text-xs text-white/60 leading-relaxed">{a.body}</p>
              <div className="flex justify-between mt-2">
                <span className="font-mono-uc text-[10px] text-white/40">{relativeTime(a.created_at)}</span>
                <span className="font-mono-uc text-[10px] text-white/40 uppercase">{(a.sent_via || []).join(" · ")}</span>
              </div>
            </div>
          ))}
          {alerts.length === 0 && <div className="text-white/40 text-xs italic text-center py-8">No alerts yet.</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
